import React, { useEffect, useState } from 'react';
import { Popover } from 'antd';
import { HexColorPicker } from 'react-colorful';
import { NODE_COLORS } from '../../Shapes/shapeCatalog';
import { POPUP_Z } from '../panelLayers';
import styles from './color.module.css';

// Lower case, like the picker's output and the panel's values, so the current colour is marked.
const PRESETS = ['#1f2329', '#ffffff', ...Object.values(NODE_COLORS), '#ec4899', '#14b8a6'].map((hex) => hex.toLowerCase());
const HEX_DIGITS = /^[0-9a-f]{6}$/;

const expandHex = (hex) => (hex.length === 3 ? hex.replace(/./g, '$&$&') : hex);

/** Typed hex code, applied on Enter or blur; anything invalid snaps back. */
const HexInput = ({ label, value, onChange, disabled }) => {
    const [draft, setDraft] = useState(value.slice(1));
    useEffect(() => setDraft(value.slice(1)), [value]);

    const commit = () => {
        const hex = expandHex(draft.replace('#', '').toLowerCase());
        if (HEX_DIGITS.test(hex)) onChange(`#${hex}`);
        else setDraft(value.slice(1));
    };

    return (
        <input
            className={styles.hexInput}
            aria-label={`${label} hex code`}
            disabled={disabled}
            spellCheck={false}
            maxLength={7}
            value={draft.toUpperCase()}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
        />
    );
};

const Swatches = ({ title, colors, value, onChange }) => (
    <div className={styles.swatchGroup}>
        <span className={styles.swatchTitle}>{title}</span>
        <div className={styles.swatches} role="radiogroup" aria-label={title}>
            {colors.map((color) => (
                <button
                    key={color}
                    type="button"
                    role="radio"
                    aria-checked={color === value}
                    aria-label={color}
                    title={color.toUpperCase()}
                    className={`${styles.swatch} ${color === value ? styles.swatchOn : ''}`.trim()}
                    style={{ '--swatch': color }}
                    onClick={() => onChange(color)}
                />
            ))}
        </div>
    </div>
);

/**
 * Colour field: [swatch | # hex]. The swatch opens a picker with the map's colours and presets. `disabled` greys it
 * out in place, for a colour that does not apply right now.
 */
export const ColorField = ({ label, color, onChange, mapColors = [], disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const picker = (
        <div className={styles.popover}>
            <HexColorPicker className={styles.picker} color={color} onChange={onChange} />
            {mapColors.length > 0 && <Swatches title="On this map" colors={mapColors} value={color} onChange={onChange} />}
            <Swatches title="Presets" colors={PRESETS} value={color} onChange={onChange} />
        </div>
    );

    return (
        <div className={`${styles.colorField} ${disabled ? styles.colorFieldDisabled : ''}`.trim()} aria-disabled={disabled}>
            <Popover
                content={picker}
                trigger="click"
                placement="bottomLeft"
                arrow={false}
                open={isOpen && !disabled}
                onOpenChange={setIsOpen}
                zIndex={POPUP_Z}
                rootClassName={`np-theme ${styles.popoverRoot}`}
            >
                <button
                    type="button"
                    className={`${styles.chip} ${isOpen ? styles.chipOpen : ''}`.trim()}
                    aria-label={`Pick ${label.toLowerCase()}`}
                    disabled={disabled}
                >
                    <span style={{ background: color }} />
                </button>
            </Popover>
            <span className={styles.hash}>#</span>
            <HexInput label={label} value={color} onChange={onChange} disabled={disabled} />
        </div>
    );
};
